// Native staking from the vault, the read side (docs/19-staking-plan.md 5.3,
// 5.4, 6.2, 6.3 items 5 and 8; ST6b, docs/19 section 8): the validators the
// Stake page lists, a minimal VoteStateV4 reader, the program gates, each
// position's status in the user's words, and "Earned so far". Nothing here
// signs, and nothing here re-implements the SDK (sdk/ts/src/stake.ts): stake
// state, warmup and cooldown, withdraw limits and discovery all come from it.
// The signing side, with every check before a key is used, is in engine.ts.

import { type Delegation, type StakeHistory, type StakePosition, type StakeState, NOT_DEACTIVATED, STAKE_ACCOUNT_LEN, withdrawLimits } from '@thevault/sdk';
import { STAKE_ELF_SHA256, STAKING_ARTIFACTS, TOKEN_ELF_SHA256, elfSha256, matchingArtifact, programElf } from './program';
import { type Rpc, fromBase58, toBase58 } from './rpc';
import { networkLabel } from './network';
import validatorsJson from './validators.json';

export const VOTE_PROGRAM_B58 = 'Vote111111111111111111111111111111111111111';
export const STAKE_PROGRAM_B58 = 'Stake11111111111111111111111111111111111111';
export const TOKEN_PROGRAM_B58 = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';

export const SYSVAR = {
  clock: 'SysvarC1ock11111111111111111111111111111111',
  epochSchedule: 'SysvarEpochSchedu1e111111111111111111111111',
  epochRewards: 'SysvarEpochRewards1111111111111111111111111',
  rent: 'SysvarRent111111111111111111111111111111111',
  stakeHistory: 'SysvarStakeHistory1111111111111111111111111',
} as const;

/** Once the last epoch a vote account earned credits in is this many epochs old, anyone may deactivate stake on it (Stake `DeactivateDelinquent`, docs/19 5.4): the app refuses to delegate there. */
export const DELINQUENT_EPOCHS = 5n;
/** Commission above this is flagged (docs/19 5.3: the Solana Foundation Delegation Program's 5% cap). */
export const COMMISSION_FLAG_BPS = 500;

// ------------------------------------------------------------- validators

export interface ValidatorEntry {
  name: string;
  voteAccount: string;
  identity: string;
  /** What the entry's source recorded; the page always shows the commission read live. */
  commissionBps: number;
  source: string;
}

type ClusterKey = 'local' | 'devnet' | 'mainnet';
const VALIDATORS = validatorsJson as unknown as Record<ClusterKey, ValidatorEntry[]> & { comment: string };

/** The curated list for the cluster the app talks to (docs/19 5.3); empty for a cluster the list does not name. */
export function validatorsFor(rpcUrl: string): ValidatorEntry[] {
  const label = networkLabel(rpcUrl);
  const key: ClusterKey | null = label === 'Local' ? 'local' : label === 'Devnet' ? 'devnet' : label === 'Mainnet' ? 'mainnet' : null;
  return key ? VALIDATORS[key] : [];
}

/** The listed name of a vote account on this cluster, or null for a pasted one. */
export function validatorName(rpcUrl: string, vote: string): string | null {
  return validatorsFor(rpcUrl).find((v) => v.voteAccount === vote)?.name ?? null;
}

// ------------------------------------------------------------ vote accounts

/** The VoteStateV4 fields the app needs (docs/19 6.3 item 5). */
export interface VoteInfo {
  nodePubkey: string;
  /** SIMD-0291's inflation-rewards commission, in basis points. */
  commissionBps: number;
  /** `(epoch, credits, previousCredits)`, oldest first, as the account stores them. */
  epochCredits: { epoch: bigint; credits: bigint; previous: bigint }[];
}

/**
 * A minimal VoteStateV4 reader (bincode, `VoteStateVersions::V4`, tag 3):
 * node_pubkey, authorized_withdrawer, inflation_rewards_collector,
 * block_revenue_collector (32 bytes each), inflation_rewards_commission_bps
 * u16, block_revenue_commission_bps u16, pending_delegator_rewards u64,
 * bls_pubkey_compressed Option<[u8; 48]>, votes VecDeque<LandedVote> (u64
 * length, 13 bytes each), root_slot Option<u64>, authorized_voters (u64
 * length, (u64, 32 bytes) each), epoch_credits Vec<(u64, u64, u64)>. Reads
 * nothing past epoch_credits. Throws on any other version or a short buffer.
 */
export function decodeVoteStateV4(d: Uint8Array): VoteInfo {
  const v = new DataView(d.buffer, d.byteOffset, d.byteLength);
  let o = 0;
  const need = (n: number) => {
    if (o + n > d.length) throw new RangeError(`vote account data ends at ${d.length}, needs ${o + n}`);
  };
  const u64 = () => {
    need(8);
    const x = v.getBigUint64(o, true);
    o += 8;
    return x;
  };
  need(4);
  const tag = v.getUint32(0, true);
  if (tag !== 3) throw new RangeError(`vote state version tag ${tag}, not VoteStateV4 (3)`);
  o = 4;
  need(128);
  const nodePubkey = toBase58(d.slice(o, o + 32));
  o += 128;
  need(4);
  const commissionBps = v.getUint16(o, true);
  o += 4;
  u64(); // pending_delegator_rewards
  need(1);
  const bls = d[o++]!;
  if (bls === 1) {
    need(48);
    o += 48;
  } else if (bls !== 0) throw new RangeError('bad Option tag for the BLS key');
  const votes = u64();
  if (votes > 1_000n) throw new RangeError('implausible vote count');
  need(Number(votes) * 13);
  o += Number(votes) * 13;
  need(1);
  const root = d[o++]!;
  if (root === 1) u64();
  else if (root !== 0) throw new RangeError('bad Option tag for the root slot');
  const voters = u64();
  if (voters > 1_000n) throw new RangeError('implausible authorized voter count');
  need(Number(voters) * 40);
  o += Number(voters) * 40;
  const n = u64();
  if (n > 1_000n) throw new RangeError('implausible epoch credit count');
  const epochCredits: VoteInfo['epochCredits'] = [];
  for (let i = 0n; i < n; i++) epochCredits.push({ epoch: u64(), credits: u64(), previous: u64() });
  return { nodePubkey, commissionBps, epochCredits };
}

/** The newest epoch in which the vote account earned credits, or null. */
export function lastEarnedEpoch(info: VoteInfo): bigint | null {
  for (let i = info.epochCredits.length - 1; i >= 0; i--) {
    const e = info.epochCredits[i]!;
    if (e.credits > e.previous) return e.epoch;
  }
  return null;
}

/** What the on-chain check of a vote account found (docs/19 6.3 item 5): `refusal` blocks staking there; `warnings` are shown and do not. */
export interface VoteCheck {
  vote: string;
  info: VoteInfo | null;
  lastEarned: bigint | null;
  refusal: string | null;
  warnings: string[];
}

/** docs/19 6.3 item 5 and 5.4, on an account as read (null when absent), at `epoch`. */
export function checkVoteAccount(vote: string, account: { owner: Uint8Array; data: Uint8Array } | null, epoch: bigint): VoteCheck {
  const out: VoteCheck = { vote, info: null, lastEarned: null, refusal: null, warnings: [] };
  if (!account) {
    out.refusal = 'No account exists at this validator address on this network.';
    return out;
  }
  if (toBase58(account.owner) !== VOTE_PROGRAM_B58) {
    out.refusal = 'This address is not a validator vote account.';
    return out;
  }
  try {
    out.info = decodeVoteStateV4(account.data);
  } catch (e) {
    out.refusal = `This vote account could not be read in the current format (${e instanceof Error ? e.message : String(e)}).`;
    return out;
  }
  out.lastEarned = lastEarnedEpoch(out.info);
  if (out.lastEarned === null) out.refusal = 'This validator has never voted, so stake there would earn nothing.';
  else if (epoch - out.lastEarned >= DELINQUENT_EPOCHS) out.refusal = `This validator last voted in staking period ${out.lastEarned}, ${epoch - out.lastEarned} staking periods ago: stake there earns nothing, and anyone may start unstaking it.`;
  else if (epoch - out.lastEarned >= 2n) out.warnings.push(`This validator is not voting: it last voted in staking period ${out.lastEarned}. Stake there earns nothing while it does not vote.`);
  if (out.info.commissionBps > COMMISSION_FLAG_BPS) out.warnings.push(`Its commission is ${bpsText(out.info.commissionBps)}, above 5%.`);
  return out;
}

export function bpsText(bps: number): string {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
}

/** Read and check a vote account now. */
export async function readVote(rpc: Rpc, vote: string, epoch: bigint): Promise<VoteCheck> {
  let bytes: Uint8Array;
  try {
    bytes = fromBase58(vote);
    if (bytes.length !== 32) throw new Error('length');
  } catch {
    return { vote, info: null, lastEarned: null, refusal: 'Not a valid Solana address.', warnings: [] };
  }
  return checkVoteAccount(vote, await rpc.getAccountInfo(vote), epoch);
}

// ------------------------------------------------------------- program gates

/** docs/19 6.3 item 8: whether staking may be offered here, and the one line that says why not. */
export interface StakingGate {
  on: boolean;
  reason: string | null;
  vaultBuild: string | null;
  stakeSha256: string | null;
  tokenSha256: string | null;
}

/** Read the vault program, the Stake program and the Token program and compare them with the pins (program.ts). Read fresh every time, never cached (as R42 reads the vault's). */
export async function stakingGate(rpc: Rpc, programIdB58: string): Promise<StakingGate> {
  const [vault, stake, token] = await Promise.all([programElf(rpc, programIdB58), programElf(rpc, STAKE_PROGRAM_B58), programElf(rpc, TOKEN_PROGRAM_B58)]);
  const pin = vault.elf ? await matchingArtifact(vault.elf, STAKING_ARTIFACTS) : null;
  const stakeSha256 = stake.elf ? await elfSha256(stake.elf) : null;
  const tokenSha256 = token.elf ? await elfSha256(token.elf) : null;
  const out: StakingGate = { on: false, reason: null, vaultBuild: pin?.label ?? null, stakeSha256, tokenSha256 };
  if (!pin) out.reason = vault.error ? `Staking is off: the vault program could not be read (${vault.error}).` : 'Staking is off: the vault program on this network is not a build with the staking rules yet.';
  else if (stakeSha256 !== STAKE_ELF_SHA256) out.reason = stake.error ? `Staking is off: the network's Stake program could not be read (${stake.error}).` : "Staking is off: the network's Stake program differs from version 5.1.0, the one this app was tested with, so staking waits until the new version has been checked.";
  else if (tokenSha256 !== TOKEN_ELF_SHA256) out.reason = token.error ? `Staking is off: the network's Token program could not be read (${token.error}).` : "Staking is off: the network's Token program differs from the version this app was tested with, so staking waits until the new version has been checked.";
  else out.on = true;
  return out;
}

// ------------------------------------------------------------------ sysvars

export interface ClockSysvar {
  slot: bigint;
  epoch: bigint;
  unixTimestamp: bigint;
}

/** slot u64, epoch_start_timestamp i64, epoch u64, leader_schedule_epoch u64, unix_timestamp i64. */
export function parseClock(d: Uint8Array): ClockSysvar {
  if (d.length < 40) throw new RangeError('Clock sysvar shorter than 40 bytes');
  const v = new DataView(d.buffer, d.byteOffset, d.byteLength);
  return { slot: v.getBigUint64(0, true), epoch: v.getBigUint64(16, true), unixTimestamp: v.getBigInt64(32, true) };
}

/** lamports_per_byte_year u64, exemption_threshold f64, burn_percent u8. */
export interface RentSysvar {
  lamportsPerByteYear: bigint;
  exemptionThreshold: number;
}

export function parseRent(d: Uint8Array): RentSysvar {
  if (d.length < 16) throw new RangeError('Rent sysvar shorter than 16 bytes');
  const v = new DataView(d.buffer, d.byteOffset, d.byteLength);
  return { lamportsPerByteYear: v.getBigUint64(0, true), exemptionThreshold: v.getFloat64(8, true) };
}

/** Agave `Rent::minimum_balance`: `((128 + len) * lamports_per_byte_year) as f64 * exemption_threshold`, truncated. */
export function rentFor(rent: RentSysvar, len: number | bigint): bigint {
  const base = (128n + BigInt(len)) * rent.lamportsPerByteYear;
  return BigInt(Math.floor(Number(base) * rent.exemptionThreshold));
}

// ---------------------------------------------------------- positions shown

export type PositionStatus = 'Activating' | 'Earning' | 'Unstaking' | 'Ready to move';

/** A position's status in the user's words (docs/19 6.2), from its state and the cluster's StakeHistory at `epoch` (the SDK's v5.1.0 arithmetic). */
export function positionStatus(state: StakeState | null, epoch: bigint, history: StakeHistory): PositionStatus {
  if (!state || state.kind !== 'Stake') return 'Ready to move';
  const d: Delegation = state.stake.delegation;
  const a = d.activatingAndDeactivating(epoch, history);
  if (a.deactivating > 0n) return 'Unstaking';
  if (a.activating > 0n) return 'Activating';
  if (a.effective > 0n) return d.deactivationEpoch === NOT_DEACTIVATED ? 'Earning' : 'Unstaking';
  return 'Ready to move';
}

/** What a position's row shows and offers (docs/19 6.2). */
export interface PositionView {
  address: string;
  n: number | null;
  lamports: bigint;
  status: PositionStatus;
  /** The delegated amount (`delegation.stake`), 0 when never delegated. */
  delegated: bigint;
  vote: string | null;
  /** True when the account was delegated and has since been unstaked (or never delegated): another validator may be chosen. */
  inactive: boolean;
  /** What a partial withdrawal may take now (Stake v5.1.0 `process_withdraw`, the SDK's withdrawLimits). */
  withdrawable: bigint;
  /** Whether everything, rent included, can be withdrawn now (the explicit close). */
  closable: boolean;
  /** Lamports above the delegation and the rent while staked: what Collect extra moves. 0 otherwise. */
  extra: bigint;
}

export function viewPosition(p: StakePosition, epoch: bigint, history: StakeHistory, rent200: bigint): PositionView {
  const state = p.state;
  const status = positionStatus(state, epoch, history);
  const limits = state ? withdrawLimits(state, p.lamports, rent200, epoch, history) : { staked: 0n, partialMax: 0n, fullAllowed: false };
  const delegated = state?.kind === 'Stake' ? state.stake.delegation.stake : 0n;
  return {
    address: toBase58(p.address),
    n: p.n,
    lamports: p.lamports,
    status,
    delegated,
    vote: state?.kind === 'Stake' ? toBase58(state.stake.delegation.voter) : null,
    inactive: status === 'Ready to move',
    withdrawable: limits.partialMax,
    closable: limits.fullAllowed,
    extra: limits.staked > 0n ? limits.partialMax : 0n,
  };
}

// -------------------------------------------------------- earned so far

/**
 * "Earned so far" (docs/19 6.2): the account's balance minus every change its
 * own transactions made to it (funding in, withdrawals out), from
 * `getSignaturesForAddress` and `getTransaction`. What remains is what reached
 * the account outside its transactions: rewards and anything else credited to
 * it. Null when the RPC lacks the history methods or a transaction cannot be
 * read (the page then says "unavailable").
 */
export async function earnedSoFar(rpc: Rpc, address: string, balance: bigint): Promise<bigint | null> {
  try {
    const sigs = await rpc.call<{ signature: string }[]>('getSignaturesForAddress', [address, { limit: 100, commitment: 'confirmed' }]);
    if (!Array.isArray(sigs) || sigs.length >= 100) return null;
    let moved = 0n;
    for (const s of sigs) {
      const tx = await rpc.getTransactionJson(s.signature);
      const keys = tx?.transaction?.message?.accountKeys;
      const pre = tx?.meta?.preBalances;
      const post = tx?.meta?.postBalances;
      if (!keys || !pre || !post) return null;
      const i = keys.indexOf(address);
      if (i < 0) continue;
      moved += BigInt(post[i]!) - BigInt(pre[i]!);
    }
    return balance - moved;
  } catch {
    return null;
  }
}

// ------------------------------------------------- the commission at delegation

const NOTE_KEY = 'thevault-stake-commission';

/** The commission a validator had when this device delegated to it (docs/19 5.4): a local note only, absent after a restore. Never throws. */
export function commissionAtDelegation(stakeAddress: string, vote: string): number | null {
  try {
    const all = JSON.parse(localStorage.getItem(NOTE_KEY) ?? '{}') as Record<string, { vote: string; bps: number }>;
    const e = all[stakeAddress];
    return e && e.vote === vote ? e.bps : null;
  } catch {
    return null;
  }
}

export function noteCommission(stakeAddress: string, vote: string, bps: number): void {
  try {
    const all = JSON.parse(localStorage.getItem(NOTE_KEY) ?? '{}') as Record<string, { vote: string; bps: number }>;
    all[stakeAddress] = { vote, bps };
    localStorage.setItem(NOTE_KEY, JSON.stringify(all));
  } catch {
    /* a local note only */
  }
}

/** The stake account's size, for the rent line. */
export const STAKE_ACCOUNT_BYTES = Number(STAKE_ACCOUNT_LEN);
